import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Separator } from '@/components/ui/separator';
import { ArrowLeft, Calculator, DollarSign, Info, TrendingUp } from 'lucide-react';
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, AreaChart, Area, PieChart, Pie, Cell } from 'recharts';

const STORAGE_KEY = 'business_valuation_data';

export const BusinessValuationCalculator = () => {
  const [annualRevenue, setAnnualRevenue] = useState(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    return saved ? JSON.parse(saved).annualRevenue : 1000000;
  });
  
  const [annualProfit, setAnnualProfit] = useState(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    return saved ? JSON.parse(saved).annualProfit : 200000;
  });
  
  const [growthRate, setGrowthRate] = useState(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    return saved ? JSON.parse(saved).growthRate : 15;
  });
  
  const [discountRate, setDiscountRate] = useState(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    return saved ? JSON.parse(saved).discountRate : 12;
  });
  
  const [method, setMethod] = useState(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    return saved ? JSON.parse(saved).method : 'dcf';
  });

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({
      annualRevenue,
      annualProfit,
      growthRate,
      discountRate,
      method
    }));
  }, [annualRevenue, annualProfit, growthRate, discountRate, method]);

  const calculateValuation = () => {
    const revenue = parseFloat(annualRevenue) || 0;
    const profit = parseFloat(annualProfit) || 0;
    const growth = parseFloat(growthRate) / 100 || 0;
    const discount = parseFloat(discountRate) / 100 || 0;

    let valuation = 0;
    let lowRange = 0;
    let highRange = 0;

    if (method === 'dcf') {
      // DCF Calculation - 5 year projection with terminal value
      let presentValue = 0;
      let cashFlow = profit;
      
      for (let year = 1; year <= 5; year++) {
        cashFlow = cashFlow * (1 + growth);
        const pv = cashFlow / Math.pow(1 + discount, year);
        presentValue += pv;
      }
      
      // Terminal value (perpetuity growth method)
      const terminalGrowth = 0.03; // 3% perpetual growth
      const terminalValue = (cashFlow * (1 + terminalGrowth)) / (discount - terminalGrowth);
      const pvTerminal = terminalValue / Math.pow(1 + discount, 5);
      
      valuation = presentValue + pvTerminal;
      lowRange = valuation * 0.8;
      highRange = valuation * 1.2;
    } else {
      // Multiple-based valuation
      const revenueMultiple = 2.5; // Industry average
      const profitMultiple = 8; // Industry average
      
      const revenueValuation = revenue * revenueMultiple;
      const profitValuation = profit * profitMultiple;
      
      valuation = (revenueValuation + profitValuation) / 2;
      lowRange = Math.min(revenueValuation, profitValuation);
      highRange = Math.max(revenueValuation, profitValuation);
    }

    const profitMargin = revenue > 0 ? (profit / revenue) * 100 : 0;
    const valuationToRevenue = revenue > 0 ? valuation / revenue : 0;
    const valuationToProfit = profit > 0 ? valuation / profit : 0;

    return {
      valuation,
      lowRange,
      highRange,
      profitMargin,
      valuationToRevenue,
      valuationToProfit
    };
  };

  const generateCashFlowProjection = () => {
    const profit = parseFloat(annualProfit) || 0;
    const growth = parseFloat(growthRate) / 100 || 0;
    const discount = parseFloat(discountRate) / 100 || 0;

    const data = [];
    let cashFlow = profit;
    let cumulativePV = 0;

    data.push({
      year: 0,
      cashFlow: Math.round(profit),
      presentValue: Math.round(profit),
      cumulativePV: Math.round(profit)
    });

    for (let year = 1; year <= 10; year++) {
      cashFlow = cashFlow * (1 + growth);
      const pv = cashFlow / Math.pow(1 + discount, year);
      cumulativePV += pv;
      
      data.push({
        year: year,
        cashFlow: Math.round(cashFlow),
        presentValue: Math.round(pv),
        cumulativePV: Math.round(cumulativePV)
      });
    }
    return data;
  };

  const generateValuationSensitivity = () => {
    const profit = parseFloat(annualProfit) || 0;
    const baseGrowth = parseFloat(growthRate) / 100 || 0;
    const discount = parseFloat(discountRate) / 100 || 0;

    const growthRates = [-0.05, 0, 0.05, 0.1, 0.15, 0.2];
    
    return growthRates.map(adjustedGrowth => {
      const totalGrowth = baseGrowth + adjustedGrowth;
      let presentValue = 0;
      let cashFlow = profit;
      
      for (let year = 1; year <= 5; year++) {
        cashFlow = cashFlow * (1 + totalGrowth);
        const pv = cashFlow / Math.pow(1 + discount, year);
        presentValue += pv;
      }
      
      const terminalGrowth = 0.03;
      const terminalValue = (cashFlow * (1 + terminalGrowth)) / (discount - terminalGrowth);
      const pvTerminal = terminalValue / Math.pow(1 + discount, 5);
      const valuation = presentValue + pvTerminal;
      
      return {
        growthRate: `${(totalGrowth * 100).toFixed(0)}%`,
        valuation: Math.round(valuation)
      };
    });
  };

  const generateValuationBreakdown = () => {
    const results = calculateValuation();
    
    return [
      { name: 'Low Range', value: Math.round(results.lowRange) },
      { name: 'Fair Value', value: Math.round(results.valuation) },
      { name: 'High Range', value: Math.round(results.highRange) }
    ];
  };

  const generateDiscountRateImpact = () => {
    const profit = parseFloat(annualProfit) || 0;
    const growth = parseFloat(growthRate) / 100 || 0;

    const discountRates = [0.08, 0.10, 0.12, 0.15, 0.18, 0.20];
    
    return discountRates.map(rate => {
      let presentValue = 0;
      let cashFlow = profit;
      
      for (let year = 1; year <= 5; year++) {
        cashFlow = cashFlow * (1 + growth);
        const pv = cashFlow / Math.pow(1 + rate, year);
        presentValue += pv;
      }
      
      const terminalGrowth = 0.03;
      const terminalValue = (cashFlow * (1 + terminalGrowth)) / (rate - terminalGrowth);
      const pvTerminal = terminalValue / Math.pow(1 + rate, 5);
      const valuation = presentValue + pvTerminal;
      
      return {
        discountRate: `${(rate * 100).toFixed(0)}%`,
        valuation: Math.round(valuation)
      };
    });
  };

  const results = calculateValuation();
  const cashFlowProjection = generateCashFlowProjection();
  const valuationSensitivity = generateValuationSensitivity();
  const valuationBreakdown = generateValuationBreakdown();
  const discountRateImpact = generateDiscountRateImpact();

  const COLORS = [
    'hsl(var(--chart-1))',
    'hsl(var(--chart-2))',
    'hsl(var(--chart-3))',
    'hsl(var(--chart-4))'
  ];

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value);
  };

  return (
    <div className="min-h-screen bg-gradient-hero">
      <nav className="border-b border-border/40 bg-card/50 backdrop-blur-sm sticky top-0 z-50">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-4">
              <Link to="/">
                <Button variant="ghost" size="sm">
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Back
                </Button>
              </Link>
              <Separator orientation="vertical" className="h-6" />
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg gradient-secondary flex items-center justify-center">
                  <DollarSign className="w-5 h-5 text-white" />
                </div>
                <span className="font-display text-lg font-semibold text-foreground">
                  Business Valuation Calculator
                </span>
              </div>
            </div>
          </div>
        </div>
      </nav>

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Tabs defaultValue="calculator" className="space-y-6">
          <div className="overflow-x-auto scrollbar-hide -mx-4 px-4">
            <TabsList className="inline-flex w-auto min-w-full sm:min-w-0 bg-muted/50 p-1 h-auto">
              <TabsTrigger 
                value="calculator" 
                className="data-[state=active]:bg-secondary data-[state=active]:text-white px-6 py-3 text-sm font-medium whitespace-nowrap"
              >
                <Calculator className="w-4 h-4 mr-2" />
                Calculator & Results
              </TabsTrigger>
              <TabsTrigger 
                value="charts" 
                className="data-[state=active]:bg-secondary data-[state=active]:text-white px-6 py-3 text-sm font-medium whitespace-nowrap"
              >
                <TrendingUp className="w-4 h-4 mr-2" />
                Visual Analysis
              </TabsTrigger>
              <TabsTrigger 
                value="insights" 
                className="data-[state=active]:bg-secondary data-[state=active]:text-white px-6 py-3 text-sm font-medium whitespace-nowrap"
              >
                <Info className="w-4 h-4 mr-2" />
                Insights & Explanation
              </TabsTrigger>
            </TabsList>
          </div>

          <TabsContent value="calculator" className="space-y-6 animate-fade-in">
            <Card className="border-border/50 shadow-md">
              <CardHeader>
                <CardTitle className="text-2xl font-display">What is Business Valuation?</CardTitle>
                <CardDescription className="text-base leading-relaxed">
                  Business valuation is the process of determining the economic value of your business. This calculator 
                  uses two common methods: Discounted Cash Flow (DCF) for intrinsic value based on future cash flows, 
                  and Multiple-based valuation using industry benchmarks.
                </CardDescription>
              </CardHeader>
            </Card>

            <div className="grid lg:grid-cols-3 gap-6">
              <Card className="lg:col-span-1 border-border/50 shadow-md">
                <CardHeader>
                  <CardTitle className="font-display">Input Parameters</CardTitle>
                  <CardDescription>Enter your business metrics</CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div className="space-y-2">
                    <Label htmlFor="annualRevenue" className="text-sm font-medium">
                      Annual Revenue ($)
                    </Label>
                    <Input
                      id="annualRevenue"
                      type="number"
                      value={annualRevenue}
                      onChange={(e) => setAnnualRevenue(e.target.value)}
                      placeholder="1000000"
                      className="text-base"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="annualProfit" className="text-sm font-medium">
                      Annual Profit ($)
                    </Label>
                    <Input
                      id="annualProfit"
                      type="number"
                      value={annualProfit}
                      onChange={(e) => setAnnualProfit(e.target.value)}
                      placeholder="200000"
                      className="text-base"
                    />
                    <p className="text-xs text-muted-foreground">EBITDA or Net Income</p>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="growthRate" className="text-sm font-medium">
                      Expected Growth Rate (%)
                    </Label>
                    <Input
                      id="growthRate"
                      type="number"
                      value={growthRate}
                      onChange={(e) => setGrowthRate(e.target.value)}
                      placeholder="15"
                      step="0.1"
                      className="text-base"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="discountRate" className="text-sm font-medium">
                      Discount Rate (%)
                    </Label>
                    <Input
                      id="discountRate"
                      type="number"
                      value={discountRate}
                      onChange={(e) => setDiscountRate(e.target.value)}
                      placeholder="12"
                      step="0.1"
                      className="text-base"
                    />
                    <p className="text-xs text-muted-foreground">Required rate of return</p>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="method" className="text-sm font-medium">
                      Valuation Method
                    </Label>
                    <Select value={method} onValueChange={setMethod}>
                      <SelectTrigger id="method" className="text-base">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="dcf">DCF (Discounted Cash Flow)</SelectItem>
                        <SelectItem value="multiple">Multiple-based</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <Button 
                    className="w-full gradient-secondary hover:opacity-90 transition-opacity shadow-secondary"
                    onClick={() => setAnnualRevenue(annualRevenue)}
                  >
                    <Calculator className="w-4 h-4 mr-2" />
                    Calculate
                  </Button>
                </CardContent>
              </Card>

              <div className="lg:col-span-2 space-y-6">
                <Card className="border-border/50 shadow-md">
                  <CardHeader>
                    <CardTitle className="font-display">Business Valuation Results</CardTitle>
                    <CardDescription>Estimated value using {method === 'dcf' ? 'DCF method' : 'Multiple-based method'}</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="grid sm:grid-cols-2 gap-6">
                      <div className="p-6 rounded-xl bg-gradient-to-br from-secondary-light to-card border border-secondary/20">
                        <div className="flex items-center gap-3 mb-2">
                          <div className="w-10 h-10 rounded-lg bg-secondary flex items-center justify-center">
                            <DollarSign className="w-5 h-5 text-white" />
                          </div>
                          <p className="text-sm font-medium text-muted-foreground">Estimated Fair Value</p>
                        </div>
                        <p className="text-3xl font-display font-bold text-secondary">
                          {formatCurrency(results.valuation)}
                        </p>
                      </div>

                      <div className="p-6 rounded-xl bg-gradient-to-br from-primary-light to-card border border-primary/20">
                        <div className="flex items-center gap-3 mb-2">
                          <div className="w-10 h-10 rounded-lg bg-primary flex items-center justify-center">
                            <TrendingUp className="w-5 h-5 text-white" />
                          </div>
                          <p className="text-sm font-medium text-muted-foreground">Valuation Range</p>
                        </div>
                        <p className="text-lg font-display font-bold text-primary">
                          {formatCurrency(results.lowRange)} - {formatCurrency(results.highRange)}
                        </p>
                      </div>

                      <div className="p-6 rounded-xl bg-gradient-to-br from-accent-light to-card border border-accent/20">
                        <div className="flex items-center gap-3 mb-2">
                          <div className="w-10 h-10 rounded-lg bg-accent flex items-center justify-center">
                            <Calculator className="w-5 h-5 text-white" />
                          </div>
                          <p className="text-sm font-medium text-muted-foreground">Revenue Multiple</p>
                        </div>
                        <p className="text-3xl font-display font-bold text-accent">
                          {results.valuationToRevenue.toFixed(2)}x
                        </p>
                      </div>

                      <div className="p-6 rounded-xl bg-gradient-to-br from-purple-light to-card border border-purple/20">
                        <div className="flex items-center gap-3 mb-2">
                          <div className="w-10 h-10 rounded-lg bg-purple flex items-center justify-center">
                            <Calculator className="w-5 h-5 text-white" />
                          </div>
                          <p className="text-sm font-medium text-muted-foreground">Profit Multiple</p>
                        </div>
                        <p className="text-3xl font-display font-bold text-purple">
                          {results.valuationToProfit.toFixed(2)}x
                        </p>
                      </div>
                    </div>

                    <Separator className="my-6" />

                    <div className="p-6 rounded-xl bg-gradient-chart">
                      <div className="grid sm:grid-cols-2 gap-6">
                        <div>
                          <p className="text-sm font-medium text-muted-foreground mb-2">Profit Margin</p>
                          <p className="text-2xl font-display font-bold text-foreground mb-1">
                            {results.profitMargin.toFixed(1)}%
                          </p>
                          <p className="text-sm text-muted-foreground">
                            {formatCurrency(parseFloat(annualProfit))} / {formatCurrency(parseFloat(annualRevenue))}
                          </p>
                        </div>
                        <div>
                          <p className="text-sm font-medium text-muted-foreground mb-2">Valuation Method</p>
                          <p className="text-2xl font-display font-bold text-foreground mb-1">
                            {method === 'dcf' ? 'DCF' : 'Multiples'}
                          </p>
                          <p className="text-sm text-muted-foreground">
                            {method === 'dcf' ? 'Future cash flow based' : 'Industry benchmark based'}
                          </p>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>
          </TabsContent>

          <TabsContent value="charts" className="space-y-6 animate-fade-in">
            <Card className="border-border/50 shadow-md">
              <CardHeader>
                <CardTitle className="text-2xl font-display">Cash Flow Projection</CardTitle>
                <CardDescription className="text-base">
                  10-year projected cash flows and present value calculation
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="h-[400px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={cashFlowProjection}>
                      <defs>
                        <linearGradient id="colorCashFlow" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="hsl(var(--secondary))" stopOpacity={0.3}/>
                          <stop offset="95%" stopColor="hsl(var(--secondary))" stopOpacity={0}/>
                        </linearGradient>
                        <linearGradient id="colorPV" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="hsl(var(--primary))" stopOpacity={0.3}/>
                          <stop offset="95%" stopColor="hsl(var(--primary))" stopOpacity={0}/>
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                      <XAxis 
                        dataKey="year" 
                        label={{ value: 'Year', position: 'insideBottom', offset: -5 }}
                        stroke="hsl(var(--muted-foreground))"
                      />
                      <YAxis 
                        label={{ value: 'Amount ($)', angle: -90, position: 'insideLeft' }}
                        stroke="hsl(var(--muted-foreground))"
                        tickFormatter={(value) => `$${(value / 1000).toFixed(0)}k`}
                      />
                      <Tooltip 
                        contentStyle={{ 
                          backgroundColor: 'hsl(var(--card))', 
                          border: '1px solid hsl(var(--border))',
                          borderRadius: '8px'
                        }}
                        formatter={(value) => formatCurrency(value)}
                      />
                      <Legend />
                      <Area 
                        type="monotone" 
                        dataKey="cashFlow" 
                        stroke="hsl(var(--secondary))" 
                        fill="url(#colorCashFlow)"
                        strokeWidth={3}
                        name="Projected Cash Flow"
                      />
                      <Area 
                        type="monotone" 
                        dataKey="presentValue" 
                        stroke="hsl(var(--primary))" 
                        fill="url(#colorPV)"
                        strokeWidth={2}
                        name="Present Value"
                      />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>

            <div className="grid lg:grid-cols-2 gap-6">
              <Card className="border-border/50 shadow-md">
                <CardHeader>
                  <CardTitle className="font-display">Growth Rate Sensitivity</CardTitle>
                  <CardDescription>
                    How growth assumptions affect valuation
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="h-[350px]">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={valuationSensitivity}>
                        <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                        <XAxis 
                          dataKey="growthRate" 
                          stroke="hsl(var(--muted-foreground))"
                        />
                        <YAxis 
                          stroke="hsl(var(--muted-foreground))"
                          tickFormatter={(value) => `$${(value / 1000000).toFixed(1)}M`}
                        />
                        <Tooltip 
                          contentStyle={{ 
                            backgroundColor: 'hsl(var(--card))', 
                            border: '1px solid hsl(var(--border))',
                            borderRadius: '8px'
                          }}
                          formatter={(value) => formatCurrency(value)}
                        />
                        <Bar dataKey="valuation" fill="hsl(var(--secondary))" name="Business Value" />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>

              <Card className="border-border/50 shadow-md">
                <CardHeader>
                  <CardTitle className="font-display">Discount Rate Impact</CardTitle>
                  <CardDescription>
                    Valuation sensitivity to required returns
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="h-[350px]">
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart data={discountRateImpact}>
                        <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                        <XAxis 
                          dataKey="discountRate" 
                          stroke="hsl(var(--muted-foreground))"
                        />
                        <YAxis 
                          stroke="hsl(var(--muted-foreground))"
                          tickFormatter={(value) => `$${(value / 1000000).toFixed(1)}M`}
                        />
                        <Tooltip 
                          contentStyle={{ 
                            backgroundColor: 'hsl(var(--card))', 
                            border: '1px solid hsl(var(--border))',
                            borderRadius: '8px'
                          }}
                          formatter={(value) => formatCurrency(value)}
                        />
                        <Legend />
                        <Line 
                          type="monotone" 
                          dataKey="valuation" 
                          stroke="hsl(var(--primary))" 
                          strokeWidth={3}
                          name="Valuation"
                          dot={{ fill: 'hsl(var(--primary))' }}
                        />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>
            </div>

            <Card className="border-border/50 shadow-md">
              <CardHeader>
                <CardTitle className="font-display">Valuation Range Breakdown</CardTitle>
                <CardDescription className="text-base">
                  Conservative to optimistic scenarios
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="h-[350px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={valuationBreakdown} layout="vertical">
                      <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                      <XAxis 
                        type="number"
                        stroke="hsl(var(--muted-foreground))"
                        tickFormatter={(value) => `$${(value / 1000000).toFixed(1)}M`}
                      />
                      <YAxis 
                        type="category"
                        dataKey="name" 
                        stroke="hsl(var(--muted-foreground))"
                      />
                      <Tooltip 
                        contentStyle={{ 
                          backgroundColor: 'hsl(var(--card))', 
                          border: '1px solid hsl(var(--border))',
                          borderRadius: '8px'
                        }}
                        formatter={(value) => formatCurrency(value)}
                      />
                      <Bar dataKey="value" name="Valuation">
                        {valuationBreakdown.map((entry, index) => (
                          <Bar 
                            key={`bar-${index}`} 
                            fill={COLORS[index % COLORS.length]}
                          />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="insights" className="space-y-6 animate-fade-in">
            <Card className="border-border/50 shadow-md">
              <CardHeader>
                <CardTitle className="text-2xl font-display">Understanding Your Business Valuation</CardTitle>
                <CardDescription className="text-base">
                  Learn how valuation works and what drives your business value
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div>
                  <h3 className="text-xl font-display font-semibold mb-3 text-secondary">
                    Your Business Valuation
                  </h3>
                  <p className="text-base text-muted-foreground leading-relaxed mb-3">
                    Based on your inputs, your business is estimated to be worth <strong className="text-secondary">{formatCurrency(results.valuation)}</strong>, 
                    with a valuation range between <strong className="text-secondary">{formatCurrency(results.lowRange)}</strong> and <strong className="text-secondary">{formatCurrency(results.highRange)}</strong>.
                  </p>
                  <div className="p-4 rounded-lg bg-muted/50 text-sm">
                    <p className="mb-2"><strong>Method Used:</strong> {method === 'dcf' ? 'Discounted Cash Flow (DCF)' : 'Multiple-based Valuation'}</p>
                    {method === 'dcf' ? (
                      <p className="font-mono">
                        DCF = Sum of Present Values of Future Cash Flows + Terminal Value
                      </p>
                    ) : (
                      <p className="font-mono">
                        Valuation = Average of (Revenue × Multiple) and (Profit × Multiple)
                      </p>
                    )}
                  </div>
                </div>

                <Separator />

                <div>
                  <h3 className="text-xl font-display font-semibold mb-3 text-primary">
                    DCF vs Multiple-Based Valuation
                  </h3>
                  <div className="space-y-4 text-base text-muted-foreground leading-relaxed">
                    <div className="p-4 rounded-lg bg-muted/50">
                      <h4 className="font-semibold text-foreground mb-2">📊 DCF (Discounted Cash Flow)</h4>
                      <p className="mb-2">
                        <strong>Pros:</strong> Intrinsic value based on future cash generation; accounts for growth and risk through discount rate
                      </p>
                      <p>
                        <strong>Cons:</strong> Sensitive to assumptions; requires accurate projections; complex calculation
                      </p>
                    </div>
                    <div className="p-4 rounded-lg bg-muted/50">
                      <h4 className="font-semibold text-foreground mb-2">📈 Multiple-based Valuation</h4>
                      <p className="mb-2">
                        <strong>Pros:</strong> Simple and quick; market-based; easy to compare with peers
                      </p>
                      <p>
                        <strong>Cons:</strong> May not reflect unique business factors; depends on finding comparable companies
                      </p>
                    </div>
                    <p className="mt-4">
                      Your Revenue Multiple is <strong>{results.valuationToRevenue.toFixed(2)}x</strong> and Profit Multiple is <strong>{results.valuationToProfit.toFixed(2)}x</strong>. 
                      {method === 'dcf' ? ' DCF is more suitable for businesses with predictable cash flows and growth.' : ' Multiple-based is faster but should be validated against industry benchmarks.'}
                    </p>
                  </div>
                </div>

                <Separator />

                <div>
                  <h3 className="text-xl font-display font-semibold mb-3 text-accent">
                    Key Value Drivers
                  </h3>
                  <div className="space-y-4 text-base">
                    <div className="p-4 rounded-lg bg-primary-light border-l-4 border-primary">
                      <h4 className="font-semibold text-foreground mb-2">✅ Revenue Growth</h4>
                      <p className="text-muted-foreground">
                        Your {growthRate}% growth rate significantly impacts valuation. Higher, sustainable growth commands premium multiples.
                      </p>
                    </div>
                    <div className="p-4 rounded-lg bg-secondary-light border-l-4 border-secondary">
                      <h4 className="font-semibold text-foreground mb-2">✅ Profit Margins</h4>
                      <p className="text-muted-foreground">
                        At {results.profitMargin.toFixed(1)}%, your margins show {results.profitMargin > 20 ? 'strong' : results.profitMargin > 10 ? 'healthy' : 'modest'} profitability. 
                        Improving margins directly increases value.
                      </p>
                    </div>
                    <div className="p-4 rounded-lg bg-accent-light border-l-4 border-accent">
                      <h4 className="font-semibold text-foreground mb-2">✅ Risk Profile</h4>
                      <p className="text-muted-foreground">
                        Your {discountRate}% discount rate reflects perceived risk. Lower risk (diversified revenue, contracts) reduces discount rate and increases valuation.
                      </p>
                    </div>
                    <div className="p-4 rounded-lg bg-purple-light border-l-4 border-purple">
                      <h4 className="font-semibold text-foreground mb-2">✅ Market Position</h4>
                      <p className="text-muted-foreground">
                        Competitive advantages, market share, and brand strength command higher multiples from buyers.
                      </p>
                    </div>
                  </div>
                </div>

                <Separator />

                <div>
                  <h3 className="text-xl font-display font-semibold mb-3 text-destructive">
                    Important Limitations and Risks
                  </h3>
                  <div className="space-y-3 text-base text-muted-foreground leading-relaxed">
                    <div className="flex gap-3">
                      <span className="text-destructive font-semibold flex-shrink-0">⚠️</span>
                      <div>
                        <strong className="text-foreground">Assumption Sensitivity:</strong> Small changes in growth rate or discount 
                        rate can significantly alter valuation. Always run sensitivity analyses.
                      </div>
                    </div>
                    <div className="flex gap-3">
                      <span className="text-destructive font-semibold flex-shrink-0">⚠️</span>
                      <div>
                        <strong className="text-foreground">Market Conditions:</strong> Valuation multiples vary by industry, 
                        economic cycle, and investor sentiment. This calculator uses generic assumptions.
                      </div>
                    </div>
                    <div className="flex gap-3">
                      <span className="text-destructive font-semibold flex-shrink-0">⚠️</span>
                      <div>
                        <strong className="text-foreground">Intangible Assets:</strong> Brand value, intellectual property, 
                        customer relationships may not be fully captured in financial metrics.
                      </div>
                    </div>
                    <div className="flex gap-3">
                      <span className="text-destructive font-semibold flex-shrink-0">⚠️</span>
                      <div>
                        <strong className="text-foreground">Actual Sale Price:</strong> Valuation is an estimate. Actual sale 
                        price depends on negotiation, buyer motivation, deal structure, and market timing.
                      </div>
                    </div>
                  </div>
                </div>

                <Separator />

                <div>
                  <h3 className="text-xl font-display font-semibold mb-3 text-purple">
                    Increasing Your Business Value
                  </h3>
                  <div className="space-y-4 text-base">
                    <div className="p-4 rounded-lg bg-secondary-light border-l-4 border-secondary">
                      <h4 className="font-semibold text-foreground mb-2">✅ Accelerate Growth</h4>
                      <p className="text-muted-foreground">
                        Invest in marketing, expand to new markets, or develop new products to increase revenue growth rate.
                      </p>
                    </div>
                    <div className="p-4 rounded-lg bg-primary-light border-l-4 border-primary">
                      <h4 className="font-semibold text-foreground mb-2">✅ Improve Margins</h4>
                      <p className="text-muted-foreground">
                        Optimize costs, increase prices strategically, or improve operational efficiency to boost profitability.
                      </p>
                    </div>
                    <div className="p-4 rounded-lg bg-accent-light border-l-4 border-accent">
                      <h4 className="font-semibold text-foreground mb-2">✅ Reduce Risk</h4>
                      <p className="text-muted-foreground">
                        Diversify customer base, secure long-term contracts, or build recurring revenue to lower perceived risk.
                      </p>
                    </div>
                    <div className="p-4 rounded-lg bg-purple-light border-l-4 border-purple">
                      <h4 className="font-semibold text-foreground mb-2">✅ Build Systems</h4>
                      <p className="text-muted-foreground">
                        Document processes, reduce owner dependence, and create scalable operations to attract buyers.
                      </p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default BusinessValuationCalculator;
